// Package Imports
import type { TableProps } from 'antd'
import type { SorterResult } from 'antd/es/table/interface'

// Server-side list params: sort + filter serialization shared by all admin
// list tables. Backend contract (core/api/list_params_builder.py):
//   sort=field:asc|desc
//   filters=field:value           (single string -> ilike substring)
//   filters=field:["a","b"]       (list -> IN, exact match, multi)
//   filters=field:true            (boolean -> equality)
// Multiple filter parts are comma-joined. URLSearchParams handles encoding.

export type TSortOrder = 'asc' | 'desc'
export type TSortState = { field: string; order: TSortOrder } | null
export type TFilterValue = string | number | boolean
export type TFilterState = Record<string, TFilterValue[]>

export type TAdminListParams = {
  page?: number
  perPage?: number
  /** Free-text search; mapped to a single ilike filter on `searchField`. */
  search?: string
  /** Column the free-text search targets (default `name`). */
  searchField?: string
  sort?: TSortState
  filters?: TFilterState
}

const encodeFilterPart = (field: string, values: TFilterValue[]): string => {
  // Booleans/single numbers stay scalar so the backend uses `==`/ilike for
  // bool columns; string (and multi) values use the list form -> SQL IN.
  if (values.length === 1 && typeof values[0] === 'boolean') {
    return `${field}:${values[0]}`
  }
  const inner = values
    .map((v) => (typeof v === 'number' ? String(v) : JSON.stringify(String(v))))
    .join(',')
  return `${field}:[${inner}]`
}

/**
 * Build the query string (`page=…&per_page=…&filters=…&sort=…`) for an admin
 * list endpoint from structured params.
 */
export const buildAdminListQuery = (params: TAdminListParams): string => {
  const sp = new URLSearchParams()
  sp.set('page', String(params.page ?? 1))
  if (params.perPage) sp.set('per_page', String(params.perPage))

  const filterParts: string[] = []
  const search = params.search?.trim()
  if (search) {
    filterParts.push(`${params.searchField ?? 'name'}:${search}`)
  }
  if (params.filters) {
    for (const [field, values] of Object.entries(params.filters)) {
      if (values && values.length > 0) {
        filterParts.push(encodeFilterPart(field, values))
      }
    }
  }
  if (filterParts.length > 0) sp.set('filters', filterParts.join(','))
  if (params.sort) sp.set('sort', `${params.sort.field}:${params.sort.order}`)

  return sp.toString()
}

/** Map URL search params to the structured params an admin list endpoint takes. */
export const toAdminListParams = (
  search: {
    page?: number
    query?: string
    sort?: TSortState
    filters?: TFilterState
  },
  searchField = 'name'
): TAdminListParams => ({
  page: search.page ?? 1,
  search: search.query,
  searchField,
  sort: search.sort ?? null,
  filters: search.filters ?? {},
})

/** antd `sortOrder` for a column, derived from the active sort state. */
export const columnSortOrder = (
  sort: TSortState | undefined,
  field: string
): 'ascend' | 'descend' | null => {
  if (!sort || sort.field !== field) return null
  return sort.order === 'desc' ? 'descend' : 'ascend'
}

/** antd `filteredValue` for a column, derived from the active filter state. */
export const columnFilteredValue = (
  filters: TFilterState | undefined,
  field: string
): TFilterValue[] | null => filters?.[field] ?? null

// --- antd Table.onChange -> state mappers (shared by URL + local-state pages) -

type TOnChangeArgs = Parameters<NonNullable<TableProps['onChange']>>

/** Map antd `onChange` sorter arg to a single sort state (or null when cleared). */
export const mapAntdSorter = <T>(
  sorter: SorterResult<T> | SorterResult<T>[]
): TSortState => {
  const one = Array.isArray(sorter) ? sorter[0] : sorter
  if (!one || !one.order) return null
  const field = String(one.columnKey ?? one.field ?? '')
  if (!field) return null
  return { field, order: one.order === 'descend' ? 'desc' : 'asc' }
}

/** Map antd `onChange` filters arg to the active (non-empty) filter state. */
export const mapAntdFilters = (raw: TOnChangeArgs[1]): TFilterState => {
  const out: TFilterState = {}
  for (const [field, values] of Object.entries(raw ?? {})) {
    if (Array.isArray(values) && values.length > 0) {
      out[field] = values as TFilterValue[]
    }
  }
  return out
}

/**
 * antd `filters` options for a boolean column. Use with `filterMultiple={false}`
 * — boolean filters are encoded as a single scalar (`field:true`).
 */
export const booleanColumnFilters = (
  trueLabel = 'Evet',
  falseLabel = 'Hayır'
): { text: string; value: boolean }[] => [
  { text: trueLabel, value: true },
  { text: falseLabel, value: false },
]
